import { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Users, PanelLeftClose, PanelLeftOpen, X, Settings, Blocks, Sparkles, BookOpen, Calendar, PhoneCall, CreditCard } from 'lucide-react';
import logo from '@/assets/logo.jpeg';

interface AdminSidebarProps {
  adminUser: any;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

export function AdminSidebar({
  adminUser,
  sidebarOpen,
  setSidebarOpen,
  sidebarCollapsed,
  setSidebarCollapsed,
}: AdminSidebarProps) {
  const renderSidebarLinks = () => (
    <nav className="admin-sidebar-nav">
      <NavLink
        to="/admin/dashboard"
        onClick={() => setSidebarOpen(false)}
        className={({ isActive }) => `admin-sidebar-link ${isActive ? 'active' : 'inactive'}`}
        title="Dashboard"
      >
        <div className="flex items-center gap-3">
          <LayoutDashboard size={18} className="text-inherit flex-shrink-0" />
          <span className="admin-sidebar-link-text">Dashboard</span>
        </div>
      </NavLink>

      {adminUser?.role === 'SuperAdmin' && (
        <NavLink
          to="/admin/organizations"
          onClick={() => setSidebarOpen(false)}
          className={({ isActive }) => `admin-sidebar-link ${isActive ? 'active' : 'inactive'}`}
          title="Organizations"
        >
          <div className="flex items-center gap-3">
            <Users size={18} className="text-inherit flex-shrink-0" />
            <span className="admin-sidebar-link-text">Organizations</span>
          </div>
        </NavLink>
      )}

      {adminUser?.role === 'Admin' && (
        <NavLink
          to="/admin/team"
          onClick={() => setSidebarOpen(false)}
          className={({ isActive }) => `admin-sidebar-link ${isActive ? 'active' : 'inactive'}`}
          title="Team Management"
        >
          <div className="flex items-center gap-3">
            <Users size={18} className="text-inherit flex-shrink-0" />
            <span className="admin-sidebar-link-text">Team</span>
          </div>
        </NavLink>
      )}

      {adminUser?.role === 'Admin' && (
        <NavLink
          to="/admin/knowledge"
          onClick={() => setSidebarOpen(false)}
          className={({ isActive }) => `admin-sidebar-link ${isActive ? 'active' : 'inactive'}`}
          title="Knowledge Base"
        >
          <div className="flex items-center gap-3">
            <BookOpen size={18} className="text-inherit flex-shrink-0" />
            <span className="admin-sidebar-link-text">Knowledge Base</span>
          </div>
        </NavLink>
      )}

      {adminUser?.role !== 'SuperAdmin' && (
        <>
          <NavLink
            to="/admin/pre-call"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `admin-sidebar-link ${isActive ? 'active' : 'inactive'}`}
            title="Pre-Call Intel"
          >
            <div className="flex items-center gap-3">
              <Calendar size={18} className="text-inherit flex-shrink-0" />
              <span className="admin-sidebar-link-text">Pre-Call Intel</span>
            </div>
          </NavLink>

          <NavLink
            to="/admin/calls"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `admin-sidebar-link ${isActive ? 'active' : 'inactive'}`}
            title="Recent Calls"
          >
            <div className="flex items-center gap-3">
              <PhoneCall size={18} className="text-inherit flex-shrink-0" />
              <span className="admin-sidebar-link-text">Recent Calls</span>
            </div>
          </NavLink>

          <div
            className="admin-sidebar-link inactive cursor-not-allowed opacity-70"
            title="Post-Call Reports (Coming Soon)"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <Blocks size={18} className="text-inherit flex-shrink-0" />
                <span className="admin-sidebar-link-text">Post-Call Reports</span>
              </div>
              {!sidebarCollapsed && (
                <span style={{ fontSize: '9px', padding: '2px 6px', background: 'var(--admin-accent-green-dim)', color: 'var(--admin-accent-green)', borderRadius: '4px', fontWeight: 'bold' }}>SOON</span>
              )}
            </div>
          </div>
        </>
      )}

      {adminUser?.role !== 'SuperAdmin' && (
        <NavLink
          to="/admin/billing"
          onClick={() => setSidebarOpen(false)}
          className={({ isActive }) => `admin-sidebar-link ${isActive ? 'active' : 'inactive'}`}
          title="Billing"
        >
          <div className="flex items-center gap-3">
            <CreditCard size={18} className="text-inherit flex-shrink-0" />
            <span className="admin-sidebar-link-text">Billing</span>
          </div>
        </NavLink>
      )}

    </nav>
  );

  const renderSettingsLinks = () => (
    <nav className="admin-sidebar-nav">
      <NavLink
        to="/admin/copilot-settings"
        onClick={() => setSidebarOpen(false)}
        className={({ isActive }) => `admin-sidebar-link ${isActive ? 'active' : 'inactive'}`}
        title="Shark Settings"
      >
        <div className="flex items-center gap-3">
          <Sparkles size={18} className="text-inherit flex-shrink-0" />
          <span className="admin-sidebar-link-text">Shark Settings</span>
        </div>
      </NavLink>

      <NavLink
        to="/admin/integrations"
        onClick={() => setSidebarOpen(false)}
        className={({ isActive }) => `admin-sidebar-link ${isActive ? 'active' : 'inactive'}`}
        title="Integrations"
      >
        <div className="flex items-center gap-3">
          <Blocks size={18} className="text-inherit flex-shrink-0" />
          <span className="admin-sidebar-link-text">Integrations</span>
        </div>
      </NavLink>

    </nav>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`} style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Glow accent */}
        <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(0, 19, 64, 0.2) 0%, transparent 65%)', pointerEvents: 'none', zIndex: 0 }} />

        {/* Brand Logo */}
        <div className="admin-sidebar-logo flex items-center justify-between w-full relative" style={{ zIndex: 1 }}>
          <div className="flex items-center gap-3">
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--admin-sidebar-border)',
              }}
            >
              <img
                src={logo}
                alt="FINN Logo"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <span className="admin-sidebar-logo-text">
              FINN<span className="admin-sidebar-logo-accent">.AI</span>
            </span>
          </div>
          {/* Toggle Button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="admin-sidebar-toggle-btn text-slate-400 hidden md:block"
            title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            {sidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        <div className={`flex-1 w-full flex flex-col overflow-y-auto ${sidebarCollapsed ? 'gap-0' : 'gap-4'}`} style={{ scrollbarWidth: 'none' }}>
          <div>
            <p className="admin-sidebar-category-text text-[10px] font-bold text-slate-400/60 uppercase tracking-wider mb-2.5 px-1" style={{ color: 'var(--admin-card-subtext)' }}>WORKSPACE</p>
            {renderSidebarLinks()}
          </div>
          {adminUser?.role !== 'SuperAdmin' && (
            <div>
              <p className="admin-sidebar-category-text text-[10px] font-bold text-slate-400/60 uppercase tracking-wider mb-2.5 px-1" style={{ color: 'var(--admin-card-subtext)' }}>PREFERENCES</p>
              {renderSettingsLinks()}
            </div>
          )}
        </div>

        {/* Footer/User Info */}
        <Link to="/admin/profile" className="admin-sidebar-profile w-full hover:opacity-90 transition-opacity" style={{ textDecoration: 'none' }}>
          <div className="w-8 h-8 rounded-full bg-[#38bdf8]/10 text-[#38bdf8] border border-[#38bdf8]/20 flex items-center justify-center font-bold text-sm min-w-[32px]">
            {adminUser?.name ? adminUser.name.charAt(0).toUpperCase() : 'A'}
          </div>
          <div className="admin-sidebar-profile-info min-w-0 flex-1">
            <p className="text-xs font-bold text-[var(--admin-sidebar-profile-name)] truncate">{adminUser?.name || 'Administrator'}</p>
            <p className="text-[10px] font-bold text-[var(--admin-sidebar-profile-role)] truncate">{adminUser?.role || 'Admin'}</p>
          </div>
        </Link>
      </aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.3 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 md:hidden"
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="admin-sidebar mobile-open fixed top-0 bottom-0 left-0 w-[280px] p-6 z-50 flex flex-col gap-6 md:hidden"
            >
              <div className="flex items-center justify-between pb-6 border-b border-[var(--admin-sidebar-profile-border)]">
                <div className="flex items-center gap-3">
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid var(--admin-sidebar-border)',
                    }}
                  >
                    <img
                      src={logo}
                      alt="FINN Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                  <span
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    className="font-extrabold text-lg tracking-tight text-[var(--admin-sidebar-logo-text)]"
                  >
                    FINN<span style={{ color: 'var(--admin-logo-accent)' }}>.AI</span>
                  </span>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="text-slate-400 hover:text-[var(--admin-sidebar-logo-text)] p-1"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto flex flex-col gap-8" style={{ scrollbarWidth: 'none' }}>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider mb-2.5 px-1" style={{ color: 'var(--admin-card-subtext)' }}>WORKSPACE</p>
                  {renderSidebarLinks()}
                </div>
                {adminUser?.role !== 'SuperAdmin' && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-2.5 px-1" style={{ color: 'var(--admin-card-subtext)' }}>PREFERENCES</p>
                    {renderSettingsLinks()}
                  </div>
                )}
              </div>

              <Link to="/admin/profile" onClick={() => setSidebarOpen(false)} className="admin-sidebar-profile hover:opacity-90 transition-opacity" style={{ textDecoration: 'none' }}>
                <div className="w-8 h-8 rounded-full bg-[#38bdf8]/10 text-[#38bdf8] border border-[#38bdf8]/20 flex items-center justify-center font-bold text-sm">
                  {adminUser?.name ? adminUser.name.charAt(0).toUpperCase() : 'A'}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-[var(--admin-sidebar-profile-name)] truncate">{adminUser?.name || 'Administrator'}</p>
                  <p className="text-[10px] font-bold text-[var(--admin-sidebar-profile-role)] truncate">{adminUser?.role || 'Admin'}</p>
                </div>
              </Link>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
